import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "react-datepicker/dist/react-datepicker.css";

import React, { Component, createContext, useState } from "react";
import {
    Route,
    BrowserRouter as Router,
    Routes,
    Navigate
} from "react-router-dom";

import Header from "../header/header";
import LoadingIcon from "../loading-icon/loading-icon";
import NotFoundPage from "../pages/notFoundPage";
import { PcaHeading } from "../theme/typography";
import { RedirectWithLocation } from "../router-extensions";
//@ts-ignore
import TagManager from "react-gtm-module";
import Theme from "../theme/default";
//@ts-ignore
import loadable from "@loadable/component";
import forms from "../../routes/forms";
import applyPages from "../../routes/apply-pages";
import { isIE } from "../unsupported-browser/unsupported-browser";
import DeprecatedBrowser from "./deprecated-browser";

const tagManagerArgs = {
    gtmId: process.env.REACT_APP_GTM_CODE,
    auth: process.env.REACT_APP_GTM_AUTH,
    preview: process.env.REACT_APP_GTM_PREVIEW
};

tagManagerArgs.gtmId && TagManager.initialize(tagManagerArgs);

type RouteConfig = {
    path: string;
    id: number;
    timeout?: number;
};

type AppConfig = {
    forms: RouteConfig[];
    applyPages: RouteConfig[];
};

const getRoutesConfig = (): AppConfig => ({
    forms: forms.filter(f => !isNaN(f.id)),
    applyPages: applyPages.filter(cp => !isNaN(cp.id))
});

const createRoutes = (
    routes: RouteConfig[],
    Component: React.FunctionComponent<RouteConfig>
) => routes.map((props: RouteConfig) => (
    <Route
        key={props.id}
        path={props.path}
        element={<Component {...props}/>}
    />
));

const PaymentService = loadable(
    () => import("../applications/mortgages/payment-component"),
    {
        fallback: <LoadingIcon />
    }
);

const ApplyPage = loadable(() => import("../applications/apply-page"), {
    fallback: <LoadingIcon />
});

const GravityForm = loadable(() => import("../applications/gravity-form"), {
    fallback: <LoadingIcon />
});

const TrackMyOrder = loadable(() => import("../applications/tmo"), {
    fallback: <LoadingIcon />
});

class ErrorBoundary extends Component<{ children: React.ReactNode }> {
    state: { hasError: boolean };

    constructor(props: any) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: any) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    componentDidCatch(error: any, errorInfo: any) {
        // You can also log the error to an error reporting service
        console.log(error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            //@ts-ignore
            return <PcaHeading m={3}>Something went wrong.</PcaHeading>;
        }

        return this.props.children;
    }
}

export const AppContext = createContext({});
export default () => {
    const [help, setHelp] = useState<
        React.Component | React.FunctionComponent | JSX.Element | string
    >("");
    const [isTMOAuth, setIsTMOAuth] = useState<boolean>(false);
    const routesConfig = getRoutesConfig();
    const formRoutes = createRoutes(routesConfig.forms, GravityForm);
    const applyPageRoutes = createRoutes(routesConfig.applyPages, ApplyPage);

    if (isIE()) {
        return <DeprecatedBrowser help={help} browser={"Internet Explorer"} />
    }

    return (
        <AppContext.Provider value={{ setHelp, isTMOAuth, setIsTMOAuth }}>
            <div className="App">
                <Theme>
                    <ErrorBoundary>
                        <Router>
                            <Header helpScreen={help}/>
                            <Routes>
                                <Route
                                    path="/"
                                    element={<Navigate to="/pca" />}
                                />
                                <Route
                                    path="/personal-loans"
                                    element={<RedirectWithLocation to="/consumer-lending/personal-loan" />}
                                />
                                <Route
                                    path="/small-business-loan-application"
                                    element={<RedirectWithLocation to="/business-lending/small-business-loan" />}
                                />
                                <Route
                                    path="/pca"
                                    element={<RedirectWithLocation to="/current-accounts/personal-current-account" />}
                                />
                                <Route
                                    path="/mortgagearrears/payment"
                                    element={<PaymentService />}
                                />
                                {process.env.REACT_APP_TMO_ENABLED ===
                                    "true" && (
                                    <Route
                                        path="/tmo/*"
                                        element={<TrackMyOrder />}
                                    />
                                )}
                                {formRoutes}
                                {applyPageRoutes}
                                <Route
                                    path="*"
                                    element={<NotFoundPage/>}
                                />
                            </Routes>
                        </Router>
                    </ErrorBoundary>
                </Theme>
            </div>
        </AppContext.Provider>
    );
};
